import { pino } from 'pino';
import { execa } from 'execa';
import { 
  ServiceStatus, 
  ServiceOperation,
  ServiceEvent
} from './types.js';
import type { 
  DeployedService, 
  OperationResult
} from './types.js';
import { RegistryDatabase } from './database/index.js';
import { HealthChecker } from './health-checker.js';

export interface ServiceManagerOptions {
  db: RegistryDatabase;
  healthChecker: HealthChecker;
  sshConfig?: {
    user: string;
    privateKeyPath?: string;
  };
}

export class ServiceManager {
  private logger = pino({ name: 'service-manager' });
  private db: RegistryDatabase;
  private healthChecker: HealthChecker;
  private sshConfig?: ServiceManagerOptions['sshConfig'];

  constructor(options: ServiceManagerOptions) {
    this.db = options.db;
    this.healthChecker = options.healthChecker;
    this.sshConfig = options.sshConfig;
  }

  // 启动服务
  async startService(serviceId: string): Promise<OperationResult> {
    const service = this.db.getService(serviceId);
    if (!service) {
      throw new Error(`Service ${serviceId} not found`);
    }

    const startTime = Date.now();
    const result: OperationResult = {
      serviceId,
      operation: ServiceOperation.Start,
      success: false,
      timestamp: new Date().toISOString()
    };

    try {
      this.logger.info({ serviceId, serviceName: service.name }, 'Starting service');
      
      // 更新状态为启动中
      this.db.updateServiceStatus(serviceId, ServiceStatus.Starting);

      // 根据服务类型执行启动命令
      switch (service.type) {
        case 'docker':
          await this.startDockerService(service);
          break;
        case 'docker-compose':
          await this.startDockerComposeService(service);
          break;
        case 'node':
          await this.startNodeService(service);
          break;
        default:
          throw new Error(`Unsupported service type: ${service.type}`);
      }

      // 更新状态为运行中
      this.db.updateServiceStatus(serviceId, ServiceStatus.Running);
      
      // 启动健康检查
      await this.healthChecker.startHealthCheck(service);

      result.success = true;
      result.message = `Service ${service.name} started successfully`;
      result.duration = Date.now() - startTime;

      // 记录事件
      this.db.recordEvent({
        id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        serviceId,
        event: ServiceEvent.Started,
        timestamp: result.timestamp
      });

      this.logger.info({ serviceId, duration: result.duration }, 'Service started');
    } catch (error: any) {
      result.error = error.message;
      result.duration = Date.now() - startTime;
      
      // 恢复状态
      this.db.updateServiceStatus(serviceId, ServiceStatus.Stopped);
      
      this.logger.error({ serviceId, error: error.message }, 'Failed to start service');
    }

    // 记录操作结果
    this.db.recordOperation(result);
    return result;
  }

  // 停止服务
  async stopService(serviceId: string): Promise<OperationResult> {
    const service = this.db.getService(serviceId);
    if (!service) {
      throw new Error(`Service ${serviceId} not found`);
    }

    const startTime = Date.now();
    const result: OperationResult = {
      serviceId,
      operation: ServiceOperation.Stop,
      success: false,
      timestamp: new Date().toISOString()
    };

    try {
      this.logger.info({ serviceId, serviceName: service.name }, 'Stopping service');
      
      // 停止健康检查
      this.healthChecker.stopHealthCheck(serviceId);
      
      // 更新状态为停止中
      this.db.updateServiceStatus(serviceId, ServiceStatus.Stopping);

      // 根据服务类型执行停止命令
      switch (service.type) {
        case 'docker':
          await this.stopDockerService(service);
          break;
        case 'docker-compose':
          await this.stopDockerComposeService(service);
          break;
        case 'node':
          await this.stopNodeService(service);
          break;
        default:
          throw new Error(`Unsupported service type: ${service.type}`);
      }

      // 更新状态为已停止
      this.db.updateServiceStatus(serviceId, ServiceStatus.Stopped);

      result.success = true;
      result.message = `Service ${service.name} stopped successfully`;
      result.duration = Date.now() - startTime;

      // 记录事件
      this.db.recordEvent({
        id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        serviceId,
        event: ServiceEvent.Stopped,
        timestamp: result.timestamp
      });

      this.logger.info({ serviceId, duration: result.duration }, 'Service stopped');
    } catch (error: any) {
      result.error = error.message;
      result.duration = Date.now() - startTime;
      
      this.logger.error({ serviceId, error: error.message }, 'Failed to stop service');
    }

    // 记录操作结果
    this.db.recordOperation(result);
    return result;
  }

  // 重启服务
  async restartService(serviceId: string): Promise<OperationResult> {
    const stopResult = await this.stopService(serviceId);
    if (!stopResult.success) {
      return stopResult;
    }

    // 等待一下确保服务完全停止
    await new Promise(resolve => setTimeout(resolve, 2000));

    return await this.startService(serviceId);
  }

  // 销毁服务
  async destroyService(serviceId: string, confirmDestroy = false): Promise<OperationResult> {
    if (!confirmDestroy) {
      throw new Error('Destroy operation requires confirmation');
    }

    const service = this.db.getService(serviceId);
    if (!service) {
      throw new Error(`Service ${serviceId} not found`);
    }

    const startTime = Date.now();
    const result: OperationResult = {
      serviceId,
      operation: ServiceOperation.Destroy,
      success: false,
      timestamp: new Date().toISOString()
    };

    try {
      this.logger.info({ serviceId, serviceName: service.name }, 'Destroying service');
      
      // 先停止服务
      if (service.status === ServiceStatus.Running) {
        await this.stopService(serviceId);
      }

      // 根据服务类型执行销毁命令
      switch (service.type) {
        case 'docker':
          await this.destroyDockerService(service);
          break;
        case 'docker-compose':
          await this.destroyDockerComposeService(service);
          break;
        case 'node':
          await this.destroyNodeService(service);
          break;
        default:
          throw new Error(`Unsupported service type: ${service.type}`);
      }

      // 从数据库删除服务记录
      this.db.deleteService(serviceId);

      result.success = true;
      result.message = `Service ${service.name} destroyed successfully`;
      result.duration = Date.now() - startTime;

      // 记录事件（在删除前）
      this.db.recordEvent({
        id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        serviceId,
        event: ServiceEvent.Destroyed,
        timestamp: result.timestamp
      });

      this.logger.info({ serviceId, duration: result.duration }, 'Service destroyed');
    } catch (error: any) {
      result.error = error.message;
      result.duration = Date.now() - startTime;
      
      this.logger.error({ serviceId, error: error.message }, 'Failed to destroy service');
    }

    // 记录操作结果
    this.db.recordOperation(result);
    return result;
  }

  // Docker 服务操作
  private async startDockerService(service: DeployedService): Promise<void> {
    const containerName = service.metadata?.containerName || service.name;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      // 本地 Docker
      await execa('docker', ['start', containerName]);
    } else {
      // 远程 Docker
      await this.executeRemoteCommand(
        service.host,
        `sudo docker start ${containerName}`
      );
    }
  }

  private async stopDockerService(service: DeployedService): Promise<void> {
    const containerName = service.metadata?.containerName || service.name;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('docker', ['stop', containerName]);
    } else {
      await this.executeRemoteCommand(
        service.host,
        `sudo docker stop ${containerName}`
      );
    }
  }

  private async destroyDockerService(service: DeployedService): Promise<void> {
    const containerName = service.metadata?.containerName || service.name;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('docker', ['rm', '-f', containerName]);
    } else {
      await this.executeRemoteCommand(
        service.host,
        `sudo docker rm -f ${containerName}`
      );
    }
  }

  // Docker Compose 服务操作
  private async startDockerComposeService(service: DeployedService): Promise<void> {
    const projectPath = service.metadata?.projectPath;
    if (!projectPath) {
      throw new Error('Project path not found in service metadata');
    }

    const command = `cd ${projectPath} && sudo docker compose up -d`;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('sh', ['-c', command]);
    } else {
      await this.executeRemoteCommand(service.host, command);
    }
  }

  private async stopDockerComposeService(service: DeployedService): Promise<void> {
    const projectPath = service.metadata?.projectPath;
    if (!projectPath) {
      throw new Error('Project path not found in service metadata');
    }

    const command = `cd ${projectPath} && sudo docker compose stop`;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('sh', ['-c', command]);
    } else {
      await this.executeRemoteCommand(service.host, command);
    }
  }

  private async destroyDockerComposeService(service: DeployedService): Promise<void> {
    const projectPath = service.metadata?.projectPath;
    if (!projectPath) {
      throw new Error('Project path not found in service metadata');
    }

    const command = `cd ${projectPath} && sudo docker compose down -v`;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('sh', ['-c', command]);
    } else {
      await this.executeRemoteCommand(service.host, command);
    }
  }

  // Node.js 服务操作
  private async startNodeService(service: DeployedService): Promise<void> {
    const appName = service.metadata?.pm2Name || service.name;
    const command = `pm2 start ${appName}`;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('sh', ['-c', command]);
    } else {
      await this.executeRemoteCommand(service.host, command);
    }
  }

  private async stopNodeService(service: DeployedService): Promise<void> {
    const appName = service.metadata?.pm2Name || service.name;
    const command = `pm2 stop ${appName}`;
    
    if (service.host === 'localhost' || service.host === '127.0.0.1') {
      await execa('sh', ['-c', command]);
    } else {
      await this.executeRemoteCommand(service.host, command);
    }
  }

  private async destroyNodeService(service: DeployedService): Promise<void> {
    const appName = service.metadata?.pm2Name || service.name;
    const projectPath = service.metadata?.projectPath;
    
    const commands = [
      `pm2 delete ${appName}`,
      projectPath ? `rm -rf ${projectPath}` : null
    ].filter(Boolean) as string[];
    
    for (const command of commands) {
      if (service.host === 'localhost' || service.host === '127.0.0.1') {
        await execa('sh', ['-c', command]);
      } else {
        await this.executeRemoteCommand(service.host, command);
      }
    }
  }

  // 执行远程命令
  private async executeRemoteCommand(host: string, command: string): Promise<void> {
    const sshArgs = [host];
    
    if (this.sshConfig?.user) {
      sshArgs.unshift(`${this.sshConfig.user}@${host}`);
      sshArgs.shift(); // 移除原来的 host
    }
    
    if (this.sshConfig?.privateKeyPath) {
      sshArgs.unshift('-i', this.sshConfig.privateKeyPath);
    }
    
    sshArgs.push(command);
    
    await execa('ssh', sshArgs);
  }
}